import AsyncStorage from '@react-native-async-storage/async-storage';
import { auth } from '@/lib/firebase';

const KEY = '@tripflow_trip_alert_read_ids';
const scopedReadKey = (uid: string) => `${KEY}:${uid}`;

const getActiveReadKey = () => {
  const uid = auth.currentUser?.uid;
  return uid ? scopedReadKey(uid) : KEY;
};

const parseReadMap = (raw: string | null): Record<string, number> => {
  if (!raw) return {};
  const parsed = JSON.parse(raw) as unknown;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return parsed as Record<string, number>;
};

async function loadReadMapForActiveUser(): Promise<Record<string, number>> {
  const activeKey = getActiveReadKey();
  const scopedRaw = await AsyncStorage.getItem(activeKey);
  if (scopedRaw) {
    return parseReadMap(scopedRaw);
  }
  if (activeKey === KEY) {
    return {};
  }

  // One-time migration path: if scoped data is empty, hydrate from legacy global key.
  const legacyRaw = await AsyncStorage.getItem(KEY);
  if (!legacyRaw) return {};
  const legacyMap = parseReadMap(legacyRaw);
  if (Object.keys(legacyMap).length > 0) {
    await AsyncStorage.setItem(activeKey, JSON.stringify(legacyMap));
  }
  return legacyMap;
}

export async function getTripAlertReadTimestamps(): Promise<Record<string, number>> {
  try {
    return await loadReadMapForActiveUser();
  } catch {
    return {};
  }
}

async function saveTripAlertReadTimestamps(map: Record<string, number>): Promise<void> {
  await AsyncStorage.setItem(getActiveReadKey(), JSON.stringify(map));
}

export async function persistTripAlertRead(id: string, readAt: number = Date.now()): Promise<void> {
  try {
    const map = await getTripAlertReadTimestamps();
    map[id] = readAt;
    await saveTripAlertReadTimestamps(map);
  } catch (error) {
    console.error('[TripAlertReadPersistence] persist failed', error);
  }
}

export async function clearPersistedTripAlertRead(id: string): Promise<void> {
  try {
    const map = await getTripAlertReadTimestamps();
    if (!(id in map)) return;
    delete map[id];
    await saveTripAlertReadTimestamps(map);
  } catch (error) {
    console.error('[TripAlertReadPersistence] clear failed', error);
  }
}

export async function clearPersistedTripAlertReads(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  try {
    const map = await getTripAlertReadTimestamps();
    let changed = false;
    for (const id of ids) {
      if (id in map) {
        delete map[id];
        changed = true;
      }
    }
    if (changed) await saveTripAlertReadTimestamps(map);
  } catch (error) {
    console.error('[TripAlertReadPersistence] clear many failed', error);
  }
}

export async function clearAllPersistedTripAlertReads(): Promise<void> {
  try {
    await AsyncStorage.removeItem(getActiveReadKey());
  } catch (error) {
    console.error('[TripAlertReadPersistence] clear all failed', error);
  }
}

export function mergePersistedReadIntoNotifications<T extends { id: string; readAt?: number | null }>(
  notifications: T[],
  readMap: Record<string, number>,
): T[] {
  return notifications.map((n) => {
    const ts = readMap[n.id];
    if (ts == null || typeof ts !== 'number' || !Number.isFinite(ts)) return n;
    return { ...n, readAt: ts };
  });
}
